import React, { useMemo, useState } from 'react';
import {
  Box, Typography, Tabs, Tab, ToggleButtonGroup, ToggleButton,
  Table, TableHead, TableBody, TableRow, TableCell, TextField,
  IconButton, Button, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, InputLabel, FormControl,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { computeAttemptStats, parseAttemptValue, buildAttemptsByTypeFromSession, derivePersonalBests, deriveChronologicalAttempts } from '../../utils/testStats';
import { derivedSpeedMph, hasDerivedSpeed } from '../../utils/derivedSpeed';
import { unitLabel as getUnitLabel } from '../../utils/testUnits';
import {
  INVALIDATION_REASONS, invalidationReasonLabel, findReplacementAttempt,
  sessionHasCorrectionProvenance, attemptActionAvailability,
} from '../../utils/testAttemptInvalidation';
import { updateTestingSession } from '../../services/athleteService';
import { invalidateTeamTestAttempt, correctTeamTestAttempt } from '../../services/teamTestingService';
import { invalidatePersonalTestAttempt, correctPersonalTestAttempt } from '../../services/personalTestingService';

function editKeyOf(sessionId, testType, attemptNumber) {
  return `${sessionId}:${testType}:${attemptNumber}`;
}

/**
 * Drill-centric Testing History: pick a drill, see every attempt across
 * every dated session for that drill. Correcting one value edits it inline
 * — no navigation to the full session form — but behind the scenes it
 * still loads the whole session, changes the one value in memory, and
 * resubmits through update_testing_session_with_attempts(), same as any
 * other edit. There is no direct write to test_attempts here.
 *
 * Phase C.7 — also renders an "Athletic Performance" PB summary strip
 * above the drill Tabs (derivePersonalBests(), utils/testStats.js) —
 * one card per test with at least one attempt, showing the athlete's
 * best-ever result (across ALL sessions, never just the active drill's
 * own date range) and when it happened. Clicking a card jumps straight
 * to that drill's own Attempts history below — the minimum "raw
 * attempts -> history -> PB" proof this phase exists to demonstrate,
 * not a polished Football Card (no percentile, no Nerd Rating, no photo
 * — all later phases).
 *
 * Test Result Correction/Invalidation (055) — Testing History is the
 * one action surface for both the legacy full-value edit AND the new
 * Invalidate/Correct actions, per that phase's own scope decision (no
 * new page). Three material changes from that phase:
 *
 * 1. The legacy inline pencil / "Edit full session" link (both of which
 *    route through update_testing_session_with_attempts()'s destructive
 *    delete+reinsert, 005) is now PERSONAL-only — never offered for a
 *    Team-sourced session (session.team_id is set) — and never offered
 *    for a Personal session that already carries correction provenance
 *    (055 now rejects that server-side too; this is the client-side
 *    mirror so the button is never even shown for an edit the server
 *    would refuse). teamWriteAuthorityByTeamId is NOT consulted for the
 *    legacy path — it was never Team-aware and stays that way; it's
 *    used only for the new actions below.
 * 2. Invalidate/Correct actions per attempt — Team ones authorized via
 *    teamWriteAuthorityByTeamId[session.team_id] (computed by the page,
 *    same team_admin/head_coach/Program-authority/platform-admin breadth
 *    as record_team_test_attempt()'s own write check — never
 *    assistant_coach), Personal ones via the SAME entered_by-or-admin
 *    heuristic the legacy pencil already used (a deliberate, reported
 *    simplification of "any active guardian" — the RPC's own server-side
 *    check is the real, broader authority and is what actually decides;
 *    this only decides whether the BUTTON is shown to this viewer).
 * 3. An invalidated attempt is never hidden — Testing History remains
 *    the audit surface — but is visually marked (struck through, reason
 *    shown) and, if replaced, shows what it was corrected to. A
 *    replacement attempt shows a lightweight "Correction of previous
 *    result" note. Chronological numbering (displayNumber) is untouched
 *    — invalidated attempts keep their real place in history, never
 *    renumbered away.
 */
export default function DrillHistory({ athleteId, sessions, testTypes, currentUserId, isAdmin, teamWriteAuthorityByTeamId, onChanged }) {
  const [selectedKey, setSelectedKey] = useState(testTypes[0]?.key);
  const [view, setView] = useState('attempts');
  const [editing, setEditing] = useState(null); // { sessionId, testType, attemptNumber, value }
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  // Test Result Correction/Invalidation (055) — the Invalidate/Correct
  // dialog. type: 'invalidate' | 'correct'; session/attempt identify the
  // target row. Kept as one piece of state (never two) so at most one
  // dialog can ever be open at a time.
  const [actionDialog, setActionDialog] = useState(null); // { type, session, attempt }
  const [actionReason, setActionReason] = useState('');
  const [actionNote, setActionNote] = useState('');
  const [actionValue, setActionValue] = useState(''); // 'correct' only — the corrected result
  const [actionSaving, setActionSaving] = useState(false);
  const [actionError, setActionError] = useState(null);
  const navigate = useNavigate();

  const activeType = testTypes.find(t => t.key === selectedKey) || testTypes[0];

  // Flatten every session's attempts for the active drill into one list,
  // oldest date first — reads as a progression, and feeds the same shape a
  // future trend chart will want. displayNumber (deriveChronologicalAttempts,
  // utils/testStats.js) is a DISPLAY-ONLY chronological attempt number —
  // "the athlete's Nth attempt at this test on this date, across every
  // underlying testing_session" — never the stored, session-scoped
  // attempt.attempt_number, which legitimately restarts at 1 in every
  // session (Team Testing, Personal Testing, and the bulk form can each
  // create a separate session for the same athlete/test/date).
  const rows = useMemo(
    () => (activeType ? deriveChronologicalAttempts(sessions, activeType.key) : []),
    [sessions, activeType]
  );

  const dailySummary = useMemo(() => {
    if (!activeType) return [];
    const byDate = {};
    rows.forEach(({ session, attempt }) => {
      if (attempt.status === 'invalidated') return; // audit surface only, never in derived stats
      const d = session.test_date;
      if (!byDate[d]) byDate[d] = [];
      byDate[d].push(attempt.result_value);
    });
    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, values]) => ({ date, ...computeAttemptStats(values, activeType.better_direction) }));
  }, [rows, activeType]);

  // Phase C.7 — one PB per test type, across this athlete's ENTIRE
  // history (never just the active drill/date range) — the summary
  // strip above the Tabs. derivePersonalBests() (055) already skips
  // invalidated attempts internally — no filtering needed here.
  const personalBests = useMemo(() => derivePersonalBests(sessions, testTypes), [sessions, testTypes]);

  if (!activeType) return null;

  const unitLabel = getUnitLabel(activeType.unit);

  // Test Result Correction/Invalidation (055) — can THIS viewer act
  // (invalidate/correct) on an attempt belonging to THIS session? Team
  // sessions defer entirely to the page-supplied per-team authority map
  // (isAdmin folds in regardless, so admin never loses access if that
  // map is ever incomplete); Personal sessions reuse the same
  // entered_by-or-admin heuristic the legacy edit path already used.
  const canActOn = (session) => {
    if (isAdmin) return true;
    if (session.team_id) return Boolean(teamWriteAuthorityByTeamId?.[session.team_id]);
    return session.entered_by === currentUserId;
  };

  // Legacy full-value edit (delete+reinsert, update_testing_session_with_attempts)
  // — PERSONAL-only (055), and only for a session with no correction
  // provenance of its own (the server now rejects that combination too;
  // this is the client-side mirror).
  const canLegacyEdit = (session) =>
    !session.team_id && (session.entered_by === currentUserId || isAdmin) && !sessionHasCorrectionProvenance(session);

  const startEdit = (session, attempt) => {
    setEditError(null);
    setEditing({
      sessionId: session.id,
      testType: attempt.test_type,
      attemptNumber: attempt.attempt_number,
      value: String(attempt.result_value),
    });
  };

  const cancelEdit = () => { setEditing(null); setEditError(null); };

  const saveEdit = async () => {
    if (!editing) return;
    const parsed = parseAttemptValue(editing.value);
    if (parsed === null) { setEditError('Enter a positive number.'); return; }

    const session = sessions.find(s => s.id === editing.sessionId);
    if (!session) { setEditError('Session no longer available.'); return; }

    // Load the full existing session, change only this one value in
    // memory, and resubmit the complete session — the same atomic RPC
    // every other edit uses. No direct test_attempts write.
    const attemptsByType = buildAttemptsByTypeFromSession(session, testTypes.map(t => t.key));
    const arr = attemptsByType[editing.testType] || [];
    while (arr.length < editing.attemptNumber) arr.push(null);
    arr[editing.attemptNumber - 1] = parsed;
    attemptsByType[editing.testType] = arr;

    setSaving(true);
    setEditError(null);
    try {
      await updateTestingSession(session.id, { testDate: session.test_date, attemptsByType });
      setEditing(null);
      await onChanged?.(); // refetch so displayed values + recalculated stats reflect the save
    } catch (e) {
      setEditError(e.message || 'Could not save that change.');
    } finally {
      setSaving(false);
    }
  };

  // Test Result Correction/Invalidation (055) — open/close/submit the
  // Invalidate or Correct dialog for one attempt.
  const openInvalidate = (session, attempt) => {
    setActionError(null);
    setActionReason('');
    setActionNote('');
    setActionValue('');
    setActionDialog({ type: 'invalidate', session, attempt });
  };

  const openCorrect = (session, attempt) => {
    setActionError(null);
    setActionReason('');
    setActionNote('');
    setActionValue(String(attempt.result_value));
    setActionDialog({ type: 'correct', session, attempt });
  };

  const closeActionDialog = () => {
    if (actionSaving) return;
    setActionDialog(null);
    setActionError(null);
  };

  const submitAction = async () => {
    if (!actionDialog) return;
    if (!actionReason) { setActionError('Choose a reason.'); return; }

    const isTeam = Boolean(actionDialog.session.team_id);

    if (actionDialog.type === 'invalidate') {
      setActionSaving(true);
      setActionError(null);
      try {
        const fn = isTeam ? invalidateTeamTestAttempt : invalidatePersonalTestAttempt;
        await fn({ attemptId: actionDialog.attempt.id, reason: actionReason, note: actionNote.trim() || null });
        setActionDialog(null);
        await onChanged?.();
      } catch (e) {
        setActionError(e.message || 'Could not invalidate that attempt.');
      } finally {
        setActionSaving(false);
      }
      return;
    }

    // 'correct'
    const parsed = parseAttemptValue(actionValue);
    if (parsed === null) { setActionError('Enter a positive corrected value.'); return; }

    setActionSaving(true);
    setActionError(null);
    try {
      const fn = isTeam ? correctTeamTestAttempt : correctPersonalTestAttempt;
      await fn({
        attemptId: actionDialog.attempt.id,
        correctedResultValue: parsed,
        reason: actionReason,
        note: actionNote.trim() || null,
      });
      setActionDialog(null);
      await onChanged?.();
    } catch (e) {
      setActionError(e.message || 'Could not save that correction.');
    } finally {
      setActionSaving(false);
    }
  };

  // One "Edit full session" link per date group, for the secondary
  // full-edit flow (change date, add/remove whole drills, several values
  // at once) — Personal-only, no correction provenance (055).
  const sessionIdsShown = [...new Set(
    rows.filter(r => canLegacyEdit(r.session)).map(r => r.session.id)
  )];

  const jumpToTest = (key) => {
    setSelectedKey(key);
    setView('attempts');
    setEditing(null);
  };

  return (
    <Box>
      {personalBests.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 900, fontSize: '0.8rem', letterSpacing: '0.06em', color: '#e8f0f8', mb: 1.5 }}>
            ATHLETIC PERFORMANCE
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {personalBests.map(pb => {
              const unit = getUnitLabel(pb.testType.unit);
              return (
                <Box
                  key={pb.testType.key}
                  onClick={() => jumpToTest(pb.testType.key)}
                  sx={{
                    cursor: 'pointer', minWidth: 140, px: 1.5, py: 1,
                    border: pb.testType.key === activeType.key ? '1px solid #2ea84b' : '1px solid #1e2a3a',
                    borderRadius: 2, backgroundColor: 'rgba(10,20,34,0.8)',
                  }}
                >
                  <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>{pb.testType.display_name}</Typography>
                  <Typography sx={{ color: '#2ea84b', fontWeight: 800, fontSize: '0.95rem' }}>
                    PB: {pb.value} {unit}
                  </Typography>
                  <Typography sx={{ color: '#6a8aa8', fontSize: '0.72rem' }}>
                    {new Date(pb.date + 'T00:00:00').toLocaleDateString()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      <Tabs
        value={activeType.key}
        onChange={(e, v) => { setSelectedKey(v); setEditing(null); }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 2, borderBottom: '1px solid #1e2a3a', minHeight: 44,
          '& .MuiTab-root': { minHeight: 44, textTransform: 'none', fontWeight: 700, fontSize: '0.85rem', px: 2 } }}
      >
        {testTypes.map(t => <Tab key={t.key} value={t.key} label={t.display_name} />)}
      </Tabs>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
        <ToggleButtonGroup
          value={view}
          exclusive
          onChange={(e, v) => v && setView(v)}
          size="small"
          sx={{ '& .MuiToggleButton-root': { textTransform: 'none', fontWeight: 700, px: 2 } }}
        >
          <ToggleButton value="attempts">Attempts</ToggleButton>
          <ToggleButton value="summary">Daily Summary</ToggleButton>
        </ToggleButtonGroup>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            onClick={() => navigate(`/athletes/${athleteId}/test`)}
            variant="contained" color="primary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Start Testing
          </Button>
          <Button
            size="small"
            onClick={() => navigate(`/athletes/${athleteId}/tests/new`)}
            variant="outlined" color="primary"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            + Log Testing Session
          </Button>
        </Box>
      </Box>

      {rows.length === 0 ? (
        <Typography sx={{ color: '#6a8aa8', py: 3 }}>No {activeType.display_name} attempts recorded yet.</Typography>
      ) : view === 'summary' ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Best</TableCell>
                <TableCell>Average</TableCell>
                <TableCell>Worst</TableCell>
                <TableCell>Spread</TableCell>
                <TableCell>Attempts</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dailySummary.map(row => (
                <TableRow key={row.date}>
                  <TableCell>{new Date(row.date + 'T00:00:00').toLocaleDateString()}</TableCell>
                  <TableCell sx={{ color: '#2ea84b', fontWeight: 700 }}>{row.best}</TableCell>
                  <TableCell>{row.average}</TableCell>
                  <TableCell sx={{ color: '#f5a623' }}>{row.worst}</TableCell>
                  <TableCell>{row.spread}</TableCell>
                  <TableCell>{row.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Attempt</TableCell>
                <TableCell>Result</TableCell>
                {hasDerivedSpeed(activeType.key) && <TableCell>Speed</TableCell>}
                <TableCell align="right">&nbsp;</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(({ session, attempt, displayNumber }) => {
                // editKeyOf/startEdit/saveEdit all correctly keep using the
                // STORED, session-scoped attempt.attempt_number — that's
                // what update_testing_session_with_attempts() actually
                // addresses server-side. displayNumber is display-only.
                const key = editKeyOf(session.id, attempt.test_type, attempt.attempt_number);
                const isEditing = editing && editKeyOf(editing.sessionId, editing.testType, editing.attemptNumber) === key;
                const canEdit = canLegacyEdit(session) && attempt.status !== 'invalidated';
                const mph = derivedSpeedMph(activeType.key, attempt.result_value);
                const isInvalidated = attempt.status === 'invalidated';
                const replacement = isInvalidated ? findReplacementAttempt(session, attempt.id) : null;
                const { canInvalidate, canCorrect } = attemptActionAvailability(session, attempt);
                const showActions = canActOn(session) && (canInvalidate || canCorrect);

                return (
                  <TableRow key={key}>
                    <TableCell>{new Date(session.test_date + 'T00:00:00').toLocaleDateString()}</TableCell>
                    <TableCell>#{displayNumber}</TableCell>
                    <TableCell>
                      {isEditing ? (
                        <TextField
                          size="small" type="number" autoFocus
                          value={editing.value}
                          onChange={e => setEditing(prev => ({ ...prev, value: e.target.value }))}
                          inputProps={{ step: 0.01, min: 0, inputMode: 'decimal' }}
                          sx={{ width: 110 }}
                        />
                      ) : isInvalidated ? (
                        <Box>
                          <Typography component="span" sx={{ textDecoration: 'line-through', color: '#6a8aa8' }}>
                            {attempt.result_value} {unitLabel}
                          </Typography>
                          <Typography sx={{ color: '#f5a623', fontSize: '0.72rem', fontWeight: 700 }}>
                            INVALIDATED · {invalidationReasonLabel(attempt.invalidation_reason)}
                          </Typography>
                          {replacement && (
                            <Typography sx={{ color: '#2ea84b', fontSize: '0.72rem' }}>
                              Corrected to {replacement.result_value} {unitLabel}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Box>
                          {`${attempt.result_value} ${unitLabel}`}
                          {attempt.replaces_attempt_id && (
                            <Typography sx={{ color: '#6a8aa8', fontSize: '0.7rem' }}>
                              Correction of previous result
                            </Typography>
                          )}
                        </Box>
                      )}
                    </TableCell>
                    {hasDerivedSpeed(activeType.key) && <TableCell>{mph !== null ? `${mph} mph` : '—'}</TableCell>}
                    <TableCell align="right">
                      {isEditing ? (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                          <Button size="small" onClick={saveEdit} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700 }}>
                            {saving ? <CircularProgress size={14} /> : 'Save'}
                          </Button>
                          <Button size="small" onClick={cancelEdit} disabled={saving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
                            Cancel
                          </Button>
                        </Box>
                      ) : (
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {canEdit && (
                            <IconButton size="small" onClick={() => startEdit(session, attempt)} aria-label="Edit attempt" sx={{ color: '#6a8aa8' }}>
                              ✎
                            </IconButton>
                          )}
                          {showActions && canInvalidate && (
                            <Button size="small" onClick={() => openInvalidate(session, attempt)} sx={{ textTransform: 'none', color: '#f5a623', fontWeight: 700 }}>
                              Invalidate
                            </Button>
                          )}
                          {showActions && canCorrect && (
                            <Button size="small" onClick={() => openCorrect(session, attempt)} sx={{ textTransform: 'none', color: '#2ea84b', fontWeight: 700 }}>
                              Correct
                            </Button>
                          )}
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {editError && <Alert severity="error" sx={{ mt: 2 }}>{editError}</Alert>}

          <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            {sessionIdsShown.map(id => (
              <Button
                key={id}
                size="small"
                onClick={() => navigate(`/athletes/${athleteId}/tests/${id}/edit`)}
                sx={{ textTransform: 'none', color: '#6a8aa8', fontSize: '0.75rem' }}
              >
                Edit full session — {new Date(sessions.find(s => s.id === id).test_date + 'T00:00:00').toLocaleDateString()}
              </Button>
            ))}
          </Box>
        </Box>
      )}

      {/* Test Result Correction/Invalidation (055) — Invalidate/Correct
          dialog. Reason is always a selector (product preference: favor
          dropdowns over free text); note is always optional, regardless
          of which reason is chosen. */}
      <Dialog open={Boolean(actionDialog)} onClose={closeActionDialog} maxWidth="xs" fullWidth>
        <DialogTitle>{actionDialog?.type === 'correct' ? 'Correct Result' : 'Invalidate Result'}</DialogTitle>
        <DialogContent>
          {actionDialog && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Typography sx={{ color: '#6a8aa8', fontSize: '0.85rem' }}>
                Current value: <strong>{actionDialog.attempt.result_value} {unitLabel}</strong>
              </Typography>

              {actionDialog.type === 'correct' && (
                <TextField
                  label="Corrected result"
                  type="number"
                  size="small"
                  autoFocus
                  value={actionValue}
                  onChange={e => setActionValue(e.target.value)}
                  inputProps={{ step: 0.01, min: 0, inputMode: 'decimal' }}
                />
              )}

              <FormControl size="small" fullWidth>
                <InputLabel id="invalidation-reason-label">Reason</InputLabel>
                <Select
                  labelId="invalidation-reason-label"
                  label="Reason"
                  value={actionReason}
                  onChange={e => setActionReason(e.target.value)}
                >
                  {INVALIDATION_REASONS.map(r => (
                    <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Note (optional)"
                size="small"
                multiline
                minRows={2}
                value={actionNote}
                onChange={e => setActionNote(e.target.value)}
              />

              {actionError && <Alert severity="error">{actionError}</Alert>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeActionDialog} disabled={actionSaving} sx={{ textTransform: 'none', color: '#6a8aa8' }}>
            Cancel
          </Button>
          <Button onClick={submitAction} disabled={actionSaving} variant="contained" color="primary" sx={{ textTransform: 'none', fontWeight: 700 }}>
            {actionSaving ? <CircularProgress size={16} sx={{ color: '#050c16' }} /> : (actionDialog?.type === 'correct' ? 'Save Correction' : 'Invalidate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
